import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';

import {
  DocumentSearchAnalytics as DocumentSearchAnalyticsData,
  PairedComparison,
  QueryTypeRow,
  RankingMetricRow,
  ScoreRatingCorrelationRow,
  ShadowPickRow,
  ShadowPicksPayload,
  TopUniqueSelectionExamples,
  UniqueSelectionExample,
  WeightedRankingMetricRow,
} from './types';
import { PLOTLY_LAYOUT_BASE, truncate } from './chartHelpers';
import CommentHeatmapCard from './CommentHeatmapCard';

const RETRIEVER_COLOURS: Record<string, string> = {
  oasis: '#2563EB',
  semantic: '#7C3AED',
  combined_reranked: '#16A34A',
};

function colourFor(searchType: string): string {
  return RETRIEVER_COLOURS[searchType] || '#64748B';
}

const RATING_COLORSCALE = 'RdYlGn' as any;

interface Props {
  data: DocumentSearchAnalyticsData;
  generatedAt: string | null;
}

interface BucketHeatmapInput {
  id: number;
  label: string;
  documents: Array<{ document_id: string; document_label: string | null; count: number }>;
}

function buildHeatmap(buckets: BucketHeatmapInput[]): { z: number[][]; x: string[]; y: string[] } | null {
  if (buckets.length === 0) return null;
  const docLabelById = new Map<string, string>();
  buckets.forEach(b => {
    b.documents.forEach(d => {
      const label = d.document_label || d.document_id;
      if (!docLabelById.has(d.document_id)) docLabelById.set(d.document_id, label || d.document_id);
    });
  });
  const docTotals = new Map<string, number>();
  buckets.forEach(b => {
    b.documents.forEach(d => {
      docTotals.set(d.document_id, (docTotals.get(d.document_id) || 0) + d.count);
    });
  });
  const topDocIds = Array.from(docTotals.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([id]) => id);

  const z: number[][] = [];
  const yLabels: string[] = [];
  buckets.forEach(b => {
    const docCountMap = new Map(b.documents.map(d => [d.document_id, d.count] as const));
    const row = topDocIds.map(docId => docCountMap.get(docId) || 0);
    if (row.some(v => v > 0)) {
      z.push(row);
      yLabels.push(truncate(b.label, 35));
    }
  });
  const xLabels = topDocIds.map(id => truncate(docLabelById.get(id) || id, 30));
  return { z, x: xLabels, y: yLabels };
}

const DocumentSearchAnalytics: React.FC<Props> = ({ data, generatedAt }) => {
  const ratingBySearchType = data.rating_by_search_type || [];
  const uniqueSelectionRatings = data.unique_selection_ratings || [];
  const topUniqueExamples = data.top_unique_selection_examples || null;
  const overlapDistribution = data.overlap_distribution || null;
  const rankingMetrics = data.ranking_metrics || [];
  const weightedRankingMetrics = data.weighted_ranking_metrics || [];
  const pairedComparison = data.paired_comparison || null;
  const scoreCorrelation = data.score_rating_correlation || [];
  const ratingByQueryType = data.rating_by_query_type || [];
  const shadowPicks = data.shadow_picks || null;
  const commonChars = data.common_characteristics || [];
  const docsPerSituation = data.common_documents_per_situation || [];
  const docsPerAddress = data.common_documents_per_address || [];
  const avgRatingPerSituation = data.avg_rating_per_situation || [];
  const avgRatingPerAddress = data.avg_rating_per_address || [];

  const situationHeatmap = useMemo(
    () =>
      buildHeatmap(
        docsPerSituation.map(s => ({
          id: s.situation_id,
          label: s.situation_name || `Situation ${s.situation_id}`,
          documents: s.documents,
        })),
      ),
    [docsPerSituation],
  );

  const addressHeatmap = useMemo(
    () =>
      buildHeatmap(
        docsPerAddress.map(a => ({
          id: a.address_id,
          label: a.address_label || `Address ${a.address_id}`,
          documents: a.documents,
        })),
      ),
    [docsPerAddress],
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-1">Average rating by search type</h3>
          <p className="text-xs text-gray-500 mb-3">
            For each rated document we look up the same document inside the same DocumentationSearch under every
            search type and attribute the rating. Production only surfaces <code>combined_reranked</code> so the
            other types would otherwise show n=0 - this lets us compare what <code>oasis</code> or
            <code> semantic</code> alone would have served. "Top-5" only counts when the type ranked the document
            at order &le; 5.
          </p>
          {ratingBySearchType.length === 0 ? (
            <p className="text-sm text-gray-500">No documentation feedback recorded yet.</p>
          ) : (
            <Plot
              data={[
                {
                  type: 'bar',
                  name: 'Avg rating (any rank)',
                  x: ratingBySearchType.map(r => r.search_type),
                  y: ratingBySearchType.map(r => r.avg_rating ?? 0),
                  marker: { color: '#2563EB' },
                  text: ratingBySearchType.map(r => `n=${r.count}`),
                  hovertemplate:
                    '%{x}<br>avg rating: %{y:.2f}<br>%{text}<br>' +
                    'avg rank: %{customdata[0]}<br>coverage: %{customdata[1]}<extra></extra>',
                  customdata: ratingBySearchType.map(r => [
                    r.avg_rank == null ? 'n/a' : r.avg_rank.toFixed(1),
                    r.coverage == null ? 'n/a' : `${(r.coverage * 100).toFixed(0)}%`,
                  ]),
                },
                {
                  type: 'bar',
                  name: 'Avg rating (top 5)',
                  x: ratingBySearchType.map(r => r.search_type),
                  y: ratingBySearchType.map(r => r.top_5_avg_rating ?? 0),
                  marker: { color: '#7C3AED' },
                  text: ratingBySearchType.map(r => `n=${r.top_5_count ?? 0}`),
                  hovertemplate:
                    '%{x} (top 5)<br>avg rating: %{y:.2f}<br>%{text}<extra></extra>',
                },
              ] as any}
              layout={{
                ...PLOTLY_LAYOUT_BASE,
                height: 360,
                barmode: 'group',
                yaxis: { title: 'Average rating', range: [0, 5] },
                xaxis: { title: 'Search type' },
                legend: { orientation: 'h', y: -0.25 },
              }}
              config={{ responsive: true, displayModeBar: false }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-1">Commonly used characteristics</h3>
          <p className="text-xs text-gray-500 mb-3">
            Characteristics with importance &ge; 8 in CEs that drove rated document searches.
          </p>
          {commonChars.length === 0 ? (
            <p className="text-sm text-gray-500">No data yet.</p>
          ) : (
            <Plot
              data={[
                {
                  type: 'bar',
                  orientation: 'h',
                  x: commonChars.slice(0, 25).map(r => r.count).reverse(),
                  y: commonChars.slice(0, 25).map(r => truncate(r.characteristic_name, 50)).reverse(),
                  marker: { color: '#0891B2' },
                  hovertemplate: '%{y}<br>count: %{x}<extra></extra>',
                },
              ] as any}
              layout={{
                ...PLOTLY_LAYOUT_BASE,
                height: Math.max(320, Math.min(25, commonChars.length) * 24 + 80),
                xaxis: { title: 'Occurrences' },
                yaxis: { automargin: true },
                margin: { ...PLOTLY_LAYOUT_BASE.margin, l: 220 },
              }}
              config={{ responsive: true, displayModeBar: false }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-1">Avg rating when uniquely retrieved</h3>
          <p className="text-xs text-gray-500 mb-3">
            Filters to documents that <em>only</em> oasis or <em>only</em> semantic retrieved (combined_reranked
            ignored as it is the union). Tells you which retriever's unique picks tend to land best.
          </p>
          {uniqueSelectionRatings.length === 0 ? (
            <p className="text-sm text-gray-500">No uniquely-retrieved rated documents yet.</p>
          ) : (
            <Plot
              data={[
                {
                  type: 'bar',
                  x: uniqueSelectionRatings.map(r => r.search_type),
                  y: uniqueSelectionRatings.map(r => r.avg_rating ?? 0),
                  marker: {
                    color: uniqueSelectionRatings.map(r => r.avg_rating ?? 0),
                    colorscale: RATING_COLORSCALE,
                    cmin: 1,
                    cmax: 5,
                    showscale: false,
                  },
                  text: uniqueSelectionRatings.map(r => `n=${r.count}`),
                  hovertemplate: '%{x}<br>avg rating: %{y:.2f}<br>%{text}<extra></extra>',
                },
              ] as any}
              layout={{
                ...PLOTLY_LAYOUT_BASE,
                height: 340,
                yaxis: { title: 'Average rating', range: [0, 5] },
                xaxis: { title: 'Search type (unique selection)' },
              }}
              config={{ responsive: true, displayModeBar: false }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-1">Oasis vs Semantic overlap</h3>
          <p className="text-xs text-gray-500 mb-3">
            Of every (search, document) pair retrieved by either retriever, how many were unique vs shared?
            {overlapDistribution?.jaccard != null && (
              <>
                {' '}Jaccard similarity = <strong>{overlapDistribution.jaccard.toFixed(3)}</strong>
                {' '}(higher = retrievers agree more).
              </>
            )}
          </p>
          {!overlapDistribution || overlapDistribution.total_pairs === 0 ? (
            <p className="text-sm text-gray-500">No retrieval data yet.</p>
          ) : (
            <Plot
              data={[
                {
                  type: 'pie',
                  hole: 0.55,
                  values: [
                    overlapDistribution.oasis_only,
                    overlapDistribution.semantic_only,
                    overlapDistribution.both,
                  ],
                  labels: ['oasis only', 'semantic only', 'both'],
                  marker: { colors: ['#2563EB', '#7C3AED', '#16A34A'] },
                  textinfo: 'label+percent' as any,
                  hovertemplate: '%{label}<br>pairs: %{value}<br>share: %{percent}<extra></extra>',
                  sort: false,
                },
              ] as any}
              layout={{
                ...PLOTLY_LAYOUT_BASE,
                height: 340,
                margin: { t: 30, b: 20, l: 20, r: 20 },
                showlegend: true,
                legend: { orientation: 'h', y: -0.05 },
                annotations: [
                  {
                    text: `${overlapDistribution.total_pairs.toLocaleString()}<br><span style="font-size:11px;color:#6b7280">pairs</span>`,
                    x: 0.5,
                    y: 0.5,
                    font: { size: 18 },
                    showarrow: false,
                  } as any,
                ],
              }}
              config={{ responsive: true, displayModeBar: false }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          )}
        </div>
      </div>

      {renderTopUniqueExamplesCard(topUniqueExamples)}

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wider mb-1">
          Oasis vs Semantic deep dive
        </h3>
        <p className="text-xs text-blue-800">
          Rank-aware metrics, paired comparisons, score calibration and a worklist of dropped picks —
          everything below uses the same set of rated documents but slices it differently to isolate
          retriever behaviour.
        </p>
      </div>

      {renderRankingMetricsCard(rankingMetrics)}
      {renderWeightedRankingMetricsCard(weightedRankingMetrics)}
      {renderPairedComparisonCard(pairedComparison)}
      {renderScoreCorrelationCard(scoreCorrelation)}
      {renderQueryTypeCard(ratingByQueryType)}
      {renderShadowPicksCard(shadowPicks)}

      {renderHeatmapCard(
        'Common documents per situation',
        'Heatmap of document feedback counts (rows = situation, columns = top documents).',
        'situation',
        situationHeatmap,
      )}
      {renderHeatmapCard(
        'Common documents per address',
        'Heatmap of document feedback counts (rows = address, columns = top documents).',
        'address',
        addressHeatmap,
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderAvgRatingBar(
          'Average document search rating per situation',
          avgRatingPerSituation.map(r => ({
            label: r.situation_name || `Situation ${r.situation_id}`,
            count: r.count,
            avg_rating: r.avg_rating,
          })),
        )}
        {renderAvgRatingBar(
          'Average document search rating per address',
          avgRatingPerAddress.map(r => ({
            label: r.address_label || `Address ${r.address_id}`,
            count: r.count,
            avg_rating: r.avg_rating,
          })),
        )}
      </div>

      <CommentHeatmapCard
        title="Semantic heatmap of document-search feedback comments"
        description="Free-text comments operators left when rating a retrieved document, embedded with mistral-embed and projected to 2D. Dot colour shows the rating they gave; the blue contour shows comment density."
        payload={data.comment_heatmap || null}
        generatedAt={generatedAt}
        showRating
        extraHoverFields={[
          { label: 'doc', pick: p => p.document_label || p.document_id },
          { label: 'situation', pick: p => p.situation_name },
          { label: 'search type', pick: p => p.search_type },
        ]}
      />
    </div>
  );
};

function renderHeatmapCard(
  title: string,
  hint: string,
  rowKind: 'situation' | 'address',
  heatmap: { z: number[][]; x: string[]; y: string[] } | null,
) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      <p className="text-xs text-gray-500 mb-3">{hint}</p>
      {!heatmap || heatmap.z.length === 0 ? (
        <p className="text-sm text-gray-500">No data yet.</p>
      ) : (
        <Plot
          data={[
            {
              type: 'heatmap',
              z: heatmap.z,
              x: heatmap.x,
              y: heatmap.y,
              colorscale: 'Blues' as any,
              hovertemplate: `${rowKind}: %{y}<br>document: %{x}<br>count: %{z}<extra></extra>`,
              colorbar: { title: { text: 'count', side: 'right' } as any },
            },
          ] as any}
          layout={{
            ...PLOTLY_LAYOUT_BASE,
            height: Math.max(360, heatmap.y.length * 26 + 120),
            xaxis: { tickangle: -35, automargin: true },
            yaxis: { automargin: true },
            margin: { ...PLOTLY_LAYOUT_BASE.margin, b: 140, l: 220 },
          }}
          config={{ responsive: true, displayModeBar: false }}
          style={{ width: '100%' }}
          useResizeHandler
        />
      )}
    </div>
  );
}

function renderAvgRatingBar(
  title: string,
  rows: { label: string; count: number; avg_rating: number | null }[],
) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No data yet.</p>
      ) : (
        <Plot
          data={[
            {
              type: 'bar',
              orientation: 'h',
              x: rows.slice(0, 25).map(r => r.avg_rating ?? 0).reverse(),
              y: rows.slice(0, 25).map(r => truncate(r.label, 50)).reverse(),
              marker: {
                color: rows.slice(0, 25).map(r => r.avg_rating ?? 0).reverse(),
                colorscale: RATING_COLORSCALE,
                cmin: 1,
                cmax: 5,
                showscale: true,
                colorbar: { title: { text: 'avg', side: 'right' } as any },
              },
              text: rows.slice(0, 25).map(r => `n=${r.count}`).reverse(),
              hovertemplate: '%{y}<br>avg rating: %{x:.2f}<br>%{text}<extra></extra>',
            },
          ] as any}
          layout={{
            ...PLOTLY_LAYOUT_BASE,
            height: Math.max(320, Math.min(25, rows.length) * 24 + 80),
            xaxis: { title: 'Average rating', range: [0, 5] },
            yaxis: { automargin: true },
            margin: { ...PLOTLY_LAYOUT_BASE.margin, l: 220 },
          }}
          config={{ responsive: true, displayModeBar: false }}
          style={{ width: '100%' }}
          useResizeHandler
        />
      )}
    </div>
  );
}

function renderRankingMetricsCard(rows: RankingMetricRow[]) {
  const threshold = rows[0]?.relevance_threshold;
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">Rank-aware retriever quality</h3>
      <p className="text-xs text-gray-500 mb-3">
        Treats operator ratings as graded relevance; unrated docs count as 0. A doc is "relevant" for
        MRR and recall when its rating &ge; <strong>{threshold ?? '—'}</strong>. Recall uses the rated
        set as the relevance pool, so values are bounded by how complete the per-query rating coverage
        is (sparse coverage ⇒ recall is a lower bound on true recall).
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">Not enough rated documents yet.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Plot
            data={[
              {
                type: 'bar',
                name: 'MRR',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.mrr ?? 0),
                marker: { color: '#0EA5E9' },
                text: rows.map(r => `${(r.mrr ?? 0).toFixed(3)}`),
                textposition: 'outside',
                hovertemplate:
                  '%{x}<br>MRR (rel&ge;%{customdata[0]}): %{y:.3f}<br>queries: %{customdata[1]}<extra></extra>',
                customdata: rows.map(r => [r.relevance_threshold, r.queries_evaluated]),
              },
              {
                type: 'bar',
                name: 'nDCG@5',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.ndcg_at_5 ?? 0),
                marker: { color: '#2563EB' },
                text: rows.map(r => (r.ndcg_at_5 == null ? '—' : r.ndcg_at_5.toFixed(3))),
                textposition: 'outside',
                hovertemplate: '%{x}<br>nDCG@5: %{y:.3f}<extra></extra>',
              },
              {
                type: 'bar',
                name: 'nDCG@10',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.ndcg_at_10 ?? 0),
                marker: { color: '#7C3AED' },
                text: rows.map(r => (r.ndcg_at_10 == null ? '—' : r.ndcg_at_10.toFixed(3))),
                textposition: 'outside',
                hovertemplate: '%{x}<br>nDCG@10: %{y:.3f}<extra></extra>',
              },
              {
                type: 'bar',
                name: 'nDCG@20',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.ndcg_at_20 ?? 0),
                marker: { color: '#16A34A' },
                text: rows.map(r => (r.ndcg_at_20 == null ? '—' : r.ndcg_at_20.toFixed(3))),
                textposition: 'outside',
                hovertemplate: '%{x}<br>nDCG@20: %{y:.3f}<extra></extra>',
              },
            ] as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 380,
              barmode: 'group',
              title: { text: 'MRR & nDCG@k', font: { size: 13 } } as any,
              yaxis: { title: 'Score (0-1)', range: [0, 1.05] },
              xaxis: { title: 'Search type' },
              legend: { orientation: 'h', y: -0.2 },
              margin: { ...PLOTLY_LAYOUT_BASE.margin, t: 40 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
          <Plot
            data={[
              {
                type: 'bar',
                name: 'Recall@5',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.recall_at_5 ?? 0),
                marker: { color: '#2563EB' },
                text: rows.map(r => (r.recall_at_5 == null ? '—' : r.recall_at_5.toFixed(3))),
                textposition: 'outside',
                hovertemplate:
                  '%{x}<br>recall@5: %{y:.3f}<br>queries with relevant pool: %{customdata}<extra></extra>',
                customdata: rows.map(r => r.recall_at_5_queries),
              },
              {
                type: 'bar',
                name: 'Recall@10',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.recall_at_10 ?? 0),
                marker: { color: '#7C3AED' },
                text: rows.map(r => (r.recall_at_10 == null ? '—' : r.recall_at_10.toFixed(3))),
                textposition: 'outside',
                hovertemplate:
                  '%{x}<br>recall@10: %{y:.3f}<br>queries with relevant pool: %{customdata}<extra></extra>',
                customdata: rows.map(r => r.recall_at_10_queries),
              },
              {
                type: 'bar',
                name: 'Recall@20',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.recall_at_20 ?? 0),
                marker: { color: '#16A34A' },
                text: rows.map(r => (r.recall_at_20 == null ? '—' : r.recall_at_20.toFixed(3))),
                textposition: 'outside',
                hovertemplate:
                  '%{x}<br>recall@20: %{y:.3f}<br>queries with relevant pool: %{customdata}<extra></extra>',
                customdata: rows.map(r => r.recall_at_20_queries),
              },
            ] as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 380,
              barmode: 'group',
              title: { text: 'Recall@k (pool = rated docs with rel&ge;threshold)', font: { size: 13 } } as any,
              yaxis: { title: 'Recall (0-1)', range: [0, 1.05] },
              xaxis: { title: 'Search type' },
              legend: { orientation: 'h', y: -0.2 },
              margin: { ...PLOTLY_LAYOUT_BASE.margin, t: 40 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        </div>
      )}
    </div>
  );
}

function renderWeightedRankingMetricsCard(rows: WeightedRankingMetricRow[]) {
  const maxRel = rows[0]?.max_effective_relevance ?? 5;
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">
        Rank-aware retriever quality &mdash; weighted by{' '}
        <code>Documentation.relevance</code>
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        Same metrics as above but the graded relevance is{' '}
        <strong>effective_rel = rating &times; Documentation.relevance</strong> (range 0&ndash;{maxRel}).
        Catches the difference between "operator liked it in this case" and "operator liked it AND the
        doc is generally relevant" &mdash; a retriever that systematically pulls high-rating /
        low-Documentation.relevance picks will look worse here. Weighted recall uses the relevance{' '}
        <em>mass</em> in top-k divided by the query's total relevance mass, so no threshold is needed.
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">
          Not enough rated documents with non-zero Documentation.relevance yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Plot
            data={[
              {
                type: 'bar',
                name: 'Weighted MRR',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.weighted_mrr ?? 0),
                marker: { color: '#0EA5E9' },
                text: rows.map(r => (r.weighted_mrr ?? 0).toFixed(3)),
                textposition: 'outside',
                hovertemplate:
                  '%{x}<br>weighted MRR: %{y:.3f}<br>queries: %{customdata}<extra></extra>',
                customdata: rows.map(r => r.queries_evaluated),
              },
              {
                type: 'bar',
                name: 'Weighted nDCG@5',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.weighted_ndcg_at_5 ?? 0),
                marker: { color: '#2563EB' },
                text: rows.map(r =>
                  r.weighted_ndcg_at_5 == null ? '—' : r.weighted_ndcg_at_5.toFixed(3),
                ),
                textposition: 'outside',
                hovertemplate: '%{x}<br>weighted nDCG@5: %{y:.3f}<extra></extra>',
              },
              {
                type: 'bar',
                name: 'Weighted nDCG@10',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.weighted_ndcg_at_10 ?? 0),
                marker: { color: '#7C3AED' },
                text: rows.map(r =>
                  r.weighted_ndcg_at_10 == null ? '—' : r.weighted_ndcg_at_10.toFixed(3),
                ),
                textposition: 'outside',
                hovertemplate: '%{x}<br>weighted nDCG@10: %{y:.3f}<extra></extra>',
              },
              {
                type: 'bar',
                name: 'Weighted nDCG@20',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.weighted_ndcg_at_20 ?? 0),
                marker: { color: '#16A34A' },
                text: rows.map(r =>
                  r.weighted_ndcg_at_20 == null ? '—' : r.weighted_ndcg_at_20.toFixed(3),
                ),
                textposition: 'outside',
                hovertemplate: '%{x}<br>weighted nDCG@20: %{y:.3f}<extra></extra>',
              },
            ] as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 380,
              barmode: 'group',
              title: { text: 'Weighted MRR & nDCG@k', font: { size: 13 } } as any,
              yaxis: { title: 'Score (0-1)', range: [0, 1.05] },
              xaxis: { title: 'Search type' },
              legend: { orientation: 'h', y: -0.2 },
              margin: { ...PLOTLY_LAYOUT_BASE.margin, t: 40 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
          <Plot
            data={[
              {
                type: 'bar',
                name: 'Weighted Recall@5',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.weighted_recall_at_5 ?? 0),
                marker: { color: '#2563EB' },
                text: rows.map(r =>
                  r.weighted_recall_at_5 == null ? '—' : r.weighted_recall_at_5.toFixed(3),
                ),
                textposition: 'outside',
                hovertemplate:
                  '%{x}<br>weighted recall@5: %{y:.3f}<br>queries: %{customdata}<extra></extra>',
                customdata: rows.map(r => r.weighted_recall_at_5_queries),
              },
              {
                type: 'bar',
                name: 'Weighted Recall@10',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.weighted_recall_at_10 ?? 0),
                marker: { color: '#7C3AED' },
                text: rows.map(r =>
                  r.weighted_recall_at_10 == null ? '—' : r.weighted_recall_at_10.toFixed(3),
                ),
                textposition: 'outside',
                hovertemplate:
                  '%{x}<br>weighted recall@10: %{y:.3f}<br>queries: %{customdata}<extra></extra>',
                customdata: rows.map(r => r.weighted_recall_at_10_queries),
              },
              {
                type: 'bar',
                name: 'Weighted Recall@20',
                x: rows.map(r => r.search_type),
                y: rows.map(r => r.weighted_recall_at_20 ?? 0),
                marker: { color: '#16A34A' },
                text: rows.map(r =>
                  r.weighted_recall_at_20 == null ? '—' : r.weighted_recall_at_20.toFixed(3),
                ),
                textposition: 'outside',
                hovertemplate:
                  '%{x}<br>weighted recall@20: %{y:.3f}<br>queries: %{customdata}<extra></extra>',
                customdata: rows.map(r => r.weighted_recall_at_20_queries),
              },
            ] as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 380,
              barmode: 'group',
              title: {
                text: 'Weighted Recall@k (relevance mass in top-k / total)',
                font: { size: 13 },
              } as any,
              yaxis: { title: 'Recall (0-1)', range: [0, 1.05] },
              xaxis: { title: 'Search type' },
              legend: { orientation: 'h', y: -0.2 },
              margin: { ...PLOTLY_LAYOUT_BASE.margin, t: 40 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        </div>
      )}
    </div>
  );
}

function renderPairedComparisonCard(paired: PairedComparison | null) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">Per-query paired comparison (oasis vs semantic)</h3>
      <p className="text-xs text-gray-500 mb-3">
        For each rated document that both retrievers surfaced inside the same query: who ranked it
        higher? Lower rank = better. Aggregates "they agree on average" vs "they trade wins per query".
      </p>
      {!paired || paired.comparable_documents === 0 ? (
        <p className="text-sm text-gray-500">No rated documents retrieved by both retrievers yet.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Plot
            data={[
              {
                type: 'bar',
                orientation: 'h',
                name: 'oasis wins',
                x: [paired.oasis_wins],
                y: ['result'],
                marker: { color: RETRIEVER_COLOURS.oasis },
                hovertemplate: 'oasis wins: %{x}<extra></extra>',
              },
              {
                type: 'bar',
                orientation: 'h',
                name: 'ties',
                x: [paired.ties],
                y: ['result'],
                marker: { color: '#9CA3AF' },
                hovertemplate: 'ties: %{x}<extra></extra>',
              },
              {
                type: 'bar',
                orientation: 'h',
                name: 'semantic wins',
                x: [paired.semantic_wins],
                y: ['result'],
                marker: { color: RETRIEVER_COLOURS.semantic },
                hovertemplate: 'semantic wins: %{x}<extra></extra>',
              },
            ] as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 200,
              barmode: 'stack',
              showlegend: true,
              legend: { orientation: 'h', y: -0.4 },
              xaxis: {
                title: `Comparable docs: ${paired.comparable_documents} (mean Δrank ${paired.mean_rank_diff.toFixed(1)}, median ${paired.median_rank_diff})`,
              },
              yaxis: { showticklabels: false },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
          <Plot
            data={[
              {
                type: 'bar',
                x: paired.rank_diff_histogram.map(b =>
                  b.lo === b.hi ? `${b.lo}` : `[${b.lo},${b.hi})`,
                ),
                y: paired.rank_diff_histogram.map(b => b.count),
                marker: {
                  color: paired.rank_diff_histogram.map(b =>
                    b.hi <= 0 ? RETRIEVER_COLOURS.oasis : b.lo >= 1 ? RETRIEVER_COLOURS.semantic : '#9CA3AF',
                  ),
                },
                hovertemplate: 'Δrank: %{x}<br>count: %{y}<extra></extra>',
              },
            ] as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 320,
              xaxis: { title: 'Δ rank (oasis − semantic). Negative ⇒ oasis ranks better.' },
              yaxis: { title: '# comparable docs' },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        </div>
      )}
    </div>
  );
}

function renderScoreCorrelationCard(rows: ScoreRatingCorrelationRow[]) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">Internal score vs operator rating</h3>
      <p className="text-xs text-gray-500 mb-3">
        For each rated document we plot each retriever's internal similarity score against the
        operator rating. A tight monotonic shape ⇒ that retriever's score is well calibrated; a
        cloud ⇒ score carries little signal about user-perceived relevance.
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No rated documents with scores yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {rows.map(row => (
            <Plot
              key={row.search_type}
              data={[
                {
                  type: 'scatter',
                  mode: 'markers',
                  x: row.points.map(p => p.score),
                  y: row.points.map(p => p.rating + (Math.random() - 0.5) * 0.25),
                  marker: {
                    color: colourFor(row.search_type),
                    size: 7,
                    opacity: 0.55,
                    line: { color: '#fff', width: 0.5 },
                  },
                  hovertemplate: 'score: %{x:.3f}<br>rating: %{y:.0f}<extra></extra>',
                },
              ] as any}
              layout={{
                ...PLOTLY_LAYOUT_BASE,
                height: 300,
                title: {
                  text: `${row.search_type} (n=${row.n}, r=${
                    row.pearson_r == null ? 'n/a' : row.pearson_r.toFixed(2)
                  }, ρ=${row.spearman_r == null ? 'n/a' : row.spearman_r.toFixed(2)})`,
                  font: { size: 13 },
                } as any,
                xaxis: { title: 'similarity score', automargin: true },
                yaxis: { title: 'operator rating', range: [0.5, 5.5], dtick: 1 },
                margin: { ...PLOTLY_LAYOUT_BASE.margin, t: 50, b: 50, l: 50, r: 20 },
              }}
              config={{ responsive: true, displayModeBar: false }}
              style={{ width: '100%' }}
              useResizeHandler
            />
          ))}
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        y-axis is jittered ±0.25 for visibility. Correlations use raw ratings; ρ = Spearman.
      </p>
    </div>
  );
}

function renderQueryTypeCard(rows: QueryTypeRow[]) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">Stratified by query type</h3>
      <p className="text-xs text-gray-500 mb-3">
        Queries are tagged by keywords in the underlying CharacteristicsEstimation input
        (<code>GRIP</code>, <code>IBGS</code>, <code>CBRN(e)</code>, <code>NRK</code>, <code>BLEVE</code>,
        <code> GHOR</code>; everything else falls into <code>other</code>). With Jaccard so low, the
        per-query winner likely depends on the query's vocabulary — this checks whether one retriever
        wins on operational jargon and the other on paraphrased queries.
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No data yet.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Plot
            data={[
              {
                type: 'bar',
                name: 'oasis top-5 avg rating',
                x: rows.map(r => r.category),
                y: rows.map(r => r.oasis_top5_avg_rating ?? 0),
                marker: { color: RETRIEVER_COLOURS.oasis },
                text: rows.map(r => `n=${r.oasis_top5_count}`),
                hovertemplate:
                  '%{x}<br>oasis top-5 avg: %{y:.2f}<br>%{text}<extra></extra>',
              },
              {
                type: 'bar',
                name: 'semantic top-5 avg rating',
                x: rows.map(r => r.category),
                y: rows.map(r => r.semantic_top5_avg_rating ?? 0),
                marker: { color: RETRIEVER_COLOURS.semantic },
                text: rows.map(r => `n=${r.semantic_top5_count}`),
                hovertemplate:
                  '%{x}<br>semantic top-5 avg: %{y:.2f}<br>%{text}<extra></extra>',
              },
            ] as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 340,
              barmode: 'group',
              yaxis: { title: 'Avg rating when retriever puts doc in top-5', range: [0, 5] },
              xaxis: { title: 'Query category' },
              legend: { orientation: 'h', y: -0.25 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
          <Plot
            data={[
              {
                type: 'bar',
                name: 'oasis wins',
                x: rows.map(r => r.category),
                y: rows.map(r => r.oasis_wins),
                marker: { color: RETRIEVER_COLOURS.oasis },
              },
              {
                type: 'bar',
                name: 'ties',
                x: rows.map(r => r.category),
                y: rows.map(r => r.ties),
                marker: { color: '#9CA3AF' },
              },
              {
                type: 'bar',
                name: 'semantic wins',
                x: rows.map(r => r.category),
                y: rows.map(r => r.semantic_wins),
                marker: { color: RETRIEVER_COLOURS.semantic },
              },
            ] as any}
            layout={{
              ...PLOTLY_LAYOUT_BASE,
              height: 340,
              barmode: 'stack',
              yaxis: { title: '# rated docs both retrievers retrieved' },
              xaxis: { title: 'Query category' },
              legend: { orientation: 'h', y: -0.25 },
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
            useResizeHandler
          />
        </div>
      )}
    </div>
  );
}

function renderTopUniqueExamplesCard(examples: TopUniqueSelectionExamples | null) {
  if (!examples) return null;
  const hasAny = examples.oasis_only.length > 0 || examples.semantic_only.length > 0;
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">Highest-rated unique picks</h3>
      <p className="text-xs text-gray-500 mb-3">
        Top {examples.top_n} rated documents that only oasis surfaced, and only semantic surfaced
        (combined_reranked ignored as it's the union). These are the concrete examples behind the
        "Avg rating when uniquely retrieved" aggregate — useful for spot-checking what each retriever
        actually adds when the other misses it.
      </p>
      {!hasAny ? (
        <p className="text-sm text-gray-500">No uniquely-retrieved rated documents yet.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {renderUniqueExamplesList('oasis', examples.oasis_only)}
          {renderUniqueExamplesList('semantic', examples.semantic_only)}
        </div>
      )}
    </div>
  );
}

function renderUniqueExamplesList(retriever: string, rows: UniqueSelectionExample[]) {
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white"
        style={{ backgroundColor: colourFor(retriever) }}
      >
        {retriever}-only ({rows.length})
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 p-3">
          No rated documents uniquely retrieved by {retriever}.
        </p>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left">
                <th className="px-2 py-1.5 font-medium text-gray-600 text-right">Rating</th>
                <th className="px-2 py-1.5 font-medium text-gray-600 text-right">Rank</th>
                <th className="px-2 py-1.5 font-medium text-gray-600">Document</th>
                <th className="px-2 py-1.5 font-medium text-gray-600">Situation / address</th>
                <th className="px-2 py-1.5 font-medium text-gray-600 text-right">Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={`${r.documentation_search_id}-${r.document_id}-${idx}`}
                  className="odd:bg-white even:bg-gray-50 align-top"
                >
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-gray-900">
                    {r.rating}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.rank ?? '—'}</td>
                  <td className="px-2 py-1.5">
                    <div className="font-medium text-gray-900">
                      {truncate(r.document_label || r.document_id || '—', 80)}
                    </div>
                    <div className="text-[10px] text-gray-500">
                      search #{r.documentation_search_id}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-gray-700">
                    {r.situation_name && <div>{truncate(r.situation_name, 40)}</div>}
                    {r.address_label && (
                      <div className="text-[10px] text-gray-500">{truncate(r.address_label, 40)}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {r.similarity_score == null ? '—' : r.similarity_score.toFixed(3)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function renderShadowPicksCard(shadow: ShadowPicksPayload | null) {
  if (!shadow) return null;
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-1">Shadow picks: dropped by the reranker</h3>
      <p className="text-xs text-gray-500 mb-3">
        Documents that one retriever put in its top-{shadow.top_k} but <code>combined_reranked</code>
        excluded — operators never saw them, so they have no ratings. Until these are rated we can't
        answer "would oasis (or semantic) alone serve users better?" Listed below are the first{' '}
        {shadow.sample_size} candidates per retriever; total dropped picks across the rated query set:{' '}
        <strong>{shadow.oasis_total_dropped}</strong> for oasis,{' '}
        <strong>{shadow.semantic_total_dropped}</strong> for semantic.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderShadowList('oasis', shadow.oasis)}
        {renderShadowList('semantic', shadow.semantic)}
      </div>
    </div>
  );
}

function renderShadowList(retriever: string, rows: ShadowPickRow[]) {
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white"
        style={{ backgroundColor: colourFor(retriever) }}
      >
        {retriever} — uniquely surfaced, rerank dropped ({rows.length})
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 p-3">No dropped picks for this retriever.</p>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left">
                <th className="px-2 py-1.5 font-medium text-gray-600">Rank</th>
                <th className="px-2 py-1.5 font-medium text-gray-600">Document</th>
                <th className="px-2 py-1.5 font-medium text-gray-600">Situation / address</th>
                <th className="px-2 py-1.5 font-medium text-gray-600 text-right">Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.documentation_retrieved_id} className="odd:bg-white even:bg-gray-50 align-top">
                  <td className="px-2 py-1.5 tabular-nums">{r.rank}</td>
                  <td className="px-2 py-1.5">
                    <div className="font-medium text-gray-900">
                      {truncate(r.document_label || r.document_id || '—', 80)}
                    </div>
                    <div className="text-[10px] text-gray-500">search #{r.documentation_search_id}</div>
                  </td>
                  <td className="px-2 py-1.5 text-gray-700">
                    {r.situation_name && <div>{truncate(r.situation_name, 40)}</div>}
                    {r.address_label && (
                      <div className="text-[10px] text-gray-500">{truncate(r.address_label, 40)}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {r.similarity_score == null ? '—' : r.similarity_score.toFixed(3)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DocumentSearchAnalytics;
